<template>
  <div>
    <error-box v-if="error" @reload="load()" />

    <loader v-else-if="loading" />

    <template v-if="!error && !loading && tournament">
      <el-alert
        :type="tournament.registrationOpen ? 'success' : 'error'"
        show-icon
        :key="tournament.registrationOpen"
        :closable="false"
        class="mb-20"
      >
        {{
          tournament.registrationOpen
            ? $tc("tournament.applications_are_open")
            : $tc("tournament.applications_are_closed")
        }}
      </el-alert>

      <el-row :gutter="20" style="margin-bottom: 30px">
        <el-col :sm="24" :md="16">
          <TournamentPanel
            :tournament="tournament"
            :competitors="tournamentCompetitors"
            :teams="tournamentTeams"
            :clubs="clubs"
            :coaches="coaches"
            :referees="referees"
            :officials="officials"
            :info-data="infoData"
          />

          <template v-if="user.club">
            <CompetitorsPanel
              :club="user.club"
              :tournament="tournament"
              :competitors="myTournamentCompetitors"
              :loader="competitorsLoader"
              :title="$tc('tournament.my_registered_competitors')"
              :canEdit="canEdit"
              @onCompetitorsChange="loadCompetitors"
              @registration-fee="
                openRegistrationFee(
                  myTournamentCompetitors,
                  myTournamentTeams,
                  user.club.shortName
                )
              "
            />

            <TeamsPanel
              :club="user.club"
              :tournament="tournament"
              :teams="myTournamentTeams"
              :loader="teamsLoader"
              :title="$tc('tournament.my_registered_teams')"
              :canEdit="canEdit"
              @on-teams-change="loadTeams"
              @registration-fee="
                openRegistrationFee(
                  myTournamentCompetitors,
                  myTournamentTeams,
                  user.club.shortName
                )
              "
            />
          </template>

          <CompetitorsPanel
            :tournament="tournament"
            :club="null"
            :competitors="tournamentCompetitors"
            :loader="competitorsLoader"
            :title="$tc('tournament.all_registered_competitors')"
            show-club-name
            :hide-add="!user.isAdmin"
            :canEdit="user.isAdmin"
            @onCompetitorsChange="loadCompetitors"
            @registration-fee="
              openRegistrationFee(tournamentCompetitors, tournamentTeams)
            "
          />

          <TeamsPanel
            :tournament="tournament"
            :club="null"
            :teams="tournamentTeams"
            :loader="teamsLoader"
            :title="$tc('tournament.all_registered_teams')"
            show-club-name
            :hide-add="!user.isAdmin"
            :canEdit="user.isAdmin"
            @on-teams-change="loadTeams"
            @registration-fee="
              openRegistrationFee(tournamentCompetitors, tournamentTeams)
            "
          />

          <TournamentCoachesPanel
            :coaches="coaches"
            :tournament="tournament"
            :canEdit="canEdit"
            show-club-name
          />

          <TournamentRefereePanel
            :referees="referees"
            show-club-name
            :canEdit="canEdit"
            :tournament="tournament"
          />

          <TournamentOfficialPanel
            :officials="officials"
            :tournament="tournament"
            :canEdit="canEdit"
            show-club-name
          />

          <panel
            :title="`${$tc('general.club', 2)} (${clubs.length})`"
            collapsable
          >
            <template v-slot:actions>
              <el-tooltip
                v-if="user.isAdmin"
                :content="$tc('general.print')"
                placement="top"
              >
                <el-button
                  icon="el-icon-printer"
                  size="medium"
                  circle
                  @click="
                    openPrint(
                      $tc('general.club'),
                      $refs.printTable ? $refs.printTable.$el : null
                    )
                  "
                />
              </el-tooltip>
            </template>

            <ClubsTable
              :data="clubs"
              :link="clubPreviewLink"
              :hidden-fields="[
                'name',
                'address',
                'email',
                'website',
                'contact',
                'mobile',
              ]"
            />

            <PrintClubsTable
              ref="printTable"
              v-show="false" :data="clubs"
              :tournamentCompetitors="allData"
              :footerText="$tc('tournament.number_of_registered_competitors') + `: ${totalNumberOfUniqueCompetitors}`"
            />
          </panel>

          <TournamentHistory
            v-if="user.isAdmin"
            :tournament="tournament"
            :data="history || []"
          />
        </el-col>
        <el-col :sm="24" :md="8" class="right-side">
          <panel
            v-if="!$config.isWff || user.isAdmin"
            :title="$tc('general.informations')"
            :loader="competitorsLoader || teamsLoader"
          >
            <template v-slot:actions>
              <el-tooltip
                :content="$tc('general.print')"
                placement="top"
              >
                <el-button
                  icon="el-icon-printer"
                  size="medium"
                  circle
                  @click="
                    openPrint(
                      $tc('general.informations'),
                      $refs.printInfoData ? $refs.printInfoData.$el : null
                    )
                  "
                />
              </el-tooltip>
            </template>

            <data-list :list="infoData" />

            <PrintInfoData
              ref="printInfoData"
              v-show="false"
              :data="infoData"
            />
          </panel>

          <panel
            v-if="user.club"
            :title="$tc('club.my_federation_information')"
            :loader="competitorsLoader || teamsLoader"
          >
            <data-list :list="myInfoData" />
          </panel>

          <el-alert
            v-if="user.isAdmin"
            :type="settings.showDisciplines ? 'success' : 'error'"
            show-icon
            :key="settings.showDisciplines"
            :closable="false"
            class="mb-20 mt-20"
          >
            {{
              settings.showDisciplines
                ? $tc("tournament.disciplines_are_visible")
                : $tc("tournament.disciplines_are_hidden")
            }}

            <el-button class="ml-10" type="text" @click="toggleShowDisciplines">
              <b>{{ settings.showDisciplines ? $tc('general.hide') : $tc('general.show') }}</b>
            </el-button>
          </el-alert>

          <TournamentDisciplinesPanels
            v-if="showDisciplines"
            :tournament="tournament"
            :teams="tournamentTeams"
            :competitors="tournamentCompetitors"
            class="mt-20"
          />

          <TournamentClubCostDeduction
            v-if="user.isAdmin"
            :tournament="tournament"
            class="mt-20"
          />
        </el-col>
      </el-row>
    </template>

    <RegistrationFee
      v-if="registrationFeePreview"
      :tournament="tournament"
      :title="registrationFeeTitle"
      :data="registrationFeeCompetitors"
      :team-data="registrationFeeTeams"
      :show-club-name="registrationFeeShowClubName"
      @close="onRegistrationFeeClose"
    />
  </div>
</template>

<script>
import moment from 'moment'
import openPrint from '@/common/print'
import registrationFeeMixin from '@/components/tournaments/registration-fee'

import ClubsTable from '@/components/clubs/ClubsTable'
import TournamentCoachesPanel from '@/components/coaches/TournamentCoachesPanel'
import TournamentRefereePanel from '@/components/referee/TournamentRefereePanel'
import TeamsPanel from '@/components/tournaments//TeamsPanel'
import TournamentOfficialPanel from '@/components/official/TournamentOfficialPanel'
import PrintClubsTable from '@/components/clubs/PrintClubsTable'
import RegistrationFee from '@/components/tournaments/RegistrationFee'
import TournamentPanel from '@/components/tournaments/TournamentPanel'
import CompetitorsPanel from '@/components/tournaments//CompetitorsPanel'
import TournamentHistory from '@/components/tournaments/TournamentHistory'
import TournamentDisciplinesPanels from '@/components/tournaments/TournamentDisciplinesPanels'
import TournamentClubCostDeduction from '@/components/tournaments/TournamentClubCostDeduction'
import PrintInfoData from '@/components/tournaments/PrintInfoData'

export default {
  mixins: [registrationFeeMixin],

  components: {
    ClubsTable,
    TeamsPanel,
    TournamentCoachesPanel,
    TournamentRefereePanel,
    TournamentOfficialPanel,
    RegistrationFee,
    PrintClubsTable,
    TournamentPanel,
    CompetitorsPanel,
    TournamentHistory,
    TournamentClubCostDeduction,
    TournamentDisciplinesPanels,
    PrintInfoData
  },

  data () {
    return {
      error: false,
      loading: false,

      competitorsLoader: false,
      teamsLoader: false,
      historyLoader: false,

      tournamentCompetitors: [],
      tournamentTeams: [],
      history: [],
      openPrint,

      registrationFeePreview: null,
      registrationFeeTitle: null,
      registrationFeeCompetitors: null,
      registrationFeeTeams: null,
      registrationFeeShowClubName: false
    }
  },

  computed: {
    canEdit () {
      return !this.tournament.isArchived && (this.user.isAdmin || this.tournament.registrationOpen)
    },

    isWff () {
      return this.$config.isWff
    },

    allData () {
      return [
        ...this.tournamentCompetitors,
        ...this.tournamentTeams.map((item) => ({
          ...item,
          isTeam: true
        }))
      ]
    },

    myAllData () {
      return [
        ...this.myTournamentCompetitors,
        ...this.myTournamentTeams.map((item) => ({
          ...item,
          isTeam: true
        }))
      ]
    },

    registrationFee () {
      return this.calculateFee(this.allData)
    },

    myRegistrationFee () {
      return this.calculateFee(this.myAllData)
    },

    settings () {
      return this.$store.state.app.settings
    },

    showDisciplines () {
      return this.isWff || this.user.isAdmin || this.settings.showDisciplines
    },

    totalNumberOfUniqueCompetitors () {
      const allData = this.tournamentCompetitors.map(reg => parseInt(reg.competitorId))
      this.tournamentTeams.forEach(team => {
        if (team.competitor) allData.push(parseInt(team.competitorId))
        if (team.competitorTwo) allData.push(parseInt(team.competitorTwoId))
        if (team.competitorThree) allData.push(parseInt(team.competitorThreeId))
        if (team.competitorFour) allData.push(parseInt(team.competitorFourId))
      })

      return new Set(allData).size
    },

    infoData () {
      const data = [
        {
          label: this.$tc('tournament.registration_end_date'),
          text: this.tournament.endDate
            ? moment(this.tournament.endDate).format('DD.MM.YYYY HH:mm')
            : '/'
        },
        {
          label: this.$tc('tournament.number_of_registered_competitors'),
          text: this.totalNumberOfUniqueCompetitors || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_disciplines'),
          text:
            this.countDisciplinesFromRegistrations(
              this.tournamentCompetitors
            ) || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_team_disciplines'),
          text:
            this.countDisciplinesFromRegistrations(this.tournamentTeams) || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_clubs'),
          text: this.tournament.clubs?.length || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_referees'),
          text: this.referees?.length || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_officials'),
          text: this.officials?.length || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_coaches'),
          text: this.coaches?.length || '0'
        },
        {
          label: this.getTotalAwardsLabelWithCount(),
          text: this.getTotalAwardsDisplayValue()
        }
      ]

      // Add medal breakdown - always show if tournament exists
      if (this.tournament) {
        const totalGold = this.getAwardCount('total_gold')
        const totalSilver = this.getAwardCount('total_silver')
        const totalBronze = this.getAwardCount('total_bronze')

        // Always show breakdown - use backend values if available, otherwise show placeholder
        data.push({
          label: this.$tc('tournament.gold_awards'),
          text: totalGold != null ? String(totalGold) : '-'
        })
        data.push({
          label: this.$tc('tournament.silver_awards'),
          text: totalSilver != null ? String(totalSilver) : '-'
        })
        data.push({
          label: this.$tc('tournament.bronze_awards'),
          text: totalBronze != null ? String(totalBronze) : '-'
        })
      }

      const youngestCompetitor = this.getYoungestCompetitor()
      if (youngestCompetitor) {
        const age = this.calculateAge(youngestCompetitor.competitor.born)
        const ageText = age !== null ? `${age} ${age === 1 ? 'godina' : age < 5 ? 'godine' : 'godina'}` : ''
        const dateText = youngestCompetitor.competitor.born ? ` (${ageText} - ${moment(youngestCompetitor.competitor.born).format('DD.MM.YYYY')})` : ''
        data.push({
          label: this.$tc('tournament.youngest_competitor'),
          text: `${youngestCompetitor.competitor.firstName} ${youngestCompetitor.competitor.lastName}${dateText}${youngestCompetitor.club ? ` (${youngestCompetitor.club.shortName})` : ''}`
        })
      }

      const oldestCompetitor = this.getOldestCompetitor()
      if (oldestCompetitor) {
        const age = this.calculateAge(oldestCompetitor.competitor.born)
        const ageText = age !== null ? `${age} ${age === 1 ? 'godina' : age < 5 ? 'godine' : 'godina'}` : ''
        const dateText = oldestCompetitor.competitor.born ? ` (${ageText} - ${moment(oldestCompetitor.competitor.born).format('DD.MM.YYYY')})` : ''
        data.push({
          label: this.$tc('tournament.oldest_competitor'),
          text: `${oldestCompetitor.competitor.firstName} ${oldestCompetitor.competitor.lastName}${dateText}${oldestCompetitor.club ? ` (${oldestCompetitor.club.shortName})` : ''}`
        })
      }

      const clubWithMostCompetitors = this.getClubWithMostCompetitors()
      if (clubWithMostCompetitors) {
        data.push({
          label: this.$tc('tournament.club_with_most_competitors'),
          text: `${clubWithMostCompetitors.club.shortName} (${clubWithMostCompetitors.count})`
        })
      }

      const clubWithMostIndividualDisciplines = this.getClubWithMostIndividualDisciplines()
      if (clubWithMostIndividualDisciplines) {
        data.push({
          label: this.$tc('tournament.club_with_most_individual_disciplines'),
          text: `${clubWithMostIndividualDisciplines.club.shortName} (${clubWithMostIndividualDisciplines.count})`
        })
      }

      const clubWithMostTeamDisciplines = this.getClubWithMostTeamDisciplines()
      if (clubWithMostTeamDisciplines) {
        data.push({
          label: this.$tc('tournament.club_with_most_team_disciplines'),
          text: `${clubWithMostTeamDisciplines.club.shortName} (${clubWithMostTeamDisciplines.count})`
        })
      }

      if (this.user.isAdmin && this.$feature.registration_fee) {
        data.push({
          label: this.$tc('general.registration_fee'),
          text: this.$formatCurrency(this.registrationFee)
        })
      }

      return data
    },

    myInfoData () {
      const data = [
        {
          label: this.$tc('tournament.number_of_registered_competitors'),
          text: this.myTournamentCompetitors?.length || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_disciplines'),
          text:
            this.countDisciplinesFromRegistrations(
              this.myTournamentCompetitors
            ) || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_team_disciplines'),
          text:
            this.countDisciplinesFromRegistrations(this.myTournamentTeams) ||
            '0'
        }
      ]

      if (this.user.isAdmin && this.$feature.registration_fee) {
        data.push({
          label: this.$tc('general.registration_fee'),
          text: this.$formatCurrency(this.myRegistrationFee)
        })
      }

      return data
    },

    user () {
      return this.$store.state.auth.user
    },

    viewingActiveTournament () {
      return this.$route.name === 'ActiveTournament'
    },

    activeTournament () {
      return this.$store.state.app.activeTournament
    },

    tournamentId () {
      return this.viewingActiveTournament
        ? this.activeTournament?.id
        : this.$route.params.id
    },

    tournament () {
      if (this.viewingActiveTournament) return this.activeTournament

      return this.$store.getters['tournaments/get'](this.tournamentId)
    },

    myTournamentCompetitors () {
      return (
        this.tournamentCompetitors.filter(
          (team) => team.club?.id === this.user?.club?.id
        ) || []
      )
    },

    myTournamentTeams () {
      return (
        this.tournamentTeams.filter(
          (team) => team.club?.id === this.user.club?.id
        ) || []
      )
    },

    coaches () {
      return this.tournament?.coaches || []
    },

    referees () {
      return this.tournament?.referees || []
    },

    officials () {
      return this.tournament?.officials || []
    },

    clubs () {
      return this.tournament.clubs || []
    },

    allCategories () {
      return this.$store.state.categories.data
    }
  },

  watch: {
    '$route.params.id' () {
      if (!this.viewingActiveTournament) {
        this.load()
        this.setPageData()
      }
    },
    'tournament.registrationOpen' () {
      // Update page data when registration status changes to show/hide Bracket button
      if (this.tournament) {
        this.setPageData()
      }
    }
  },

  created () {
    this.load()
  },

  methods: {
    async toggleShowDisciplines () {
      try {
        const response = await this.$store.dispatch('settings/update', {
          id: this.settings.id,
          payload: {
            showDisciplines: !this.settings.showDisciplines
          }
        })
        this.$store.commit('app/updateSettings', response)
      } catch (e) {
        console.error(e)
      }
    },

    openRegistrationFee (comps, teams, title) {
      this.registrationFeeTitle = title ? ` - ${title}` : ''
      this.registrationFeeCompetitors = Array.isArray(comps) ? comps : []
      this.registrationFeeTeams = Array.isArray(teams) ? teams : []
      this.registrationFeeShowClubName = title == null || title === ''
      this.registrationFeePreview = true
    },

    onRegistrationFeeClose () {
      this.registrationFeeTitle = ''
      this.registrationFeePreview = false
      this.registrationFeeCompetitors = null
      this.registrationFeeTeams = null
      this.registrationFeeShowClubName = false
    },

    clubPreviewLink (club) {
      if (!this.user.isAdmin) {
        return { name: 'ClubView', params: { id: club.id } }
      }

      if (this.viewingActiveTournament) {
        return {
          name: 'ActiveTournamentClubView',
          params: { clubId: club.id }
        }
      }
      return {
        name: 'TournamentClubView',
        params: { id: this.tournament.id, clubId: club.id }
      }
    },

    calculateFee (registrations) {
      return registrations.reduce((acc, item) => {
        const priceWithTournamentDiscount = item.isTeam
          ? this.getTeamPrice(item, true)
          : this.getPrice(item, true)
        const priceWithoutTournamentDiscount = item.isTeam
          ? this.getTeamPrice(item, false)
          : this.getPrice(item, false)
        const deduction = item.club?.id
          ? (this.tournament?.clubDeductions?.find(d => parseInt(d.clubId) === parseInt(item.club.id))?.deduction || 0)
          : 0
        const itemPrice = deduction
          ? ((100 - deduction) / 100) * priceWithoutTournamentDiscount
          : priceWithTournamentDiscount
        return acc + itemPrice
      }, 0)
    },

    countDisciplinesFromRegistrations (registration) {
      return registration?.reduce((acc, item) => {
        const disc = item.categories.reduce(
          (a, c) => a + c.disciplines.length,
          0
        )
        return acc + disc
      }, 0)
    },

    getTotalAwardsLabelWithCount () {
      const label = this.$tc('tournament.total_podium_awards_required')
      return label
    },

    getTotalAwardsDisplayValue () {
      // Use backend value only, show empty string if not available
      const totalAwards = this.tournament?.total_awards
      if (totalAwards != null && totalAwards !== undefined) {
        return String(totalAwards)
      }
      return ''
    },

    getAwardCount (awardType) {
      if (!this.tournament) return null

      // Use backend values only
      if (this.tournament[awardType] !== undefined) {
        return this.tournament[awardType]
      }

      return null
    },

    calculateAge (birthDate) {
      if (!birthDate) return null

      const birth = new Date(birthDate)
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
      }

      return age
    },

    getYoungestCompetitor () {
      if (!this.tournamentCompetitors?.length) return null
      const competitorsWithBorn = this.tournamentCompetitors.filter(c => c.competitor?.born)
      if (!competitorsWithBorn.length) return null
      return competitorsWithBorn.reduce((youngest, current) => {
        const currentDate = new Date(current.competitor.born)
        const youngestDate = new Date(youngest.competitor.born)
        return currentDate > youngestDate ? current : youngest
      })
    },

    getOldestCompetitor () {
      if (!this.tournamentCompetitors?.length) return null
      const competitorsWithBorn = this.tournamentCompetitors.filter(c => c.competitor?.born)
      if (!competitorsWithBorn.length) return null
      return competitorsWithBorn.reduce((oldest, current) => {
        const currentDate = new Date(current.competitor.born)
        const oldestDate = new Date(oldest.competitor.born)
        return currentDate < oldestDate ? current : oldest
      })
    },

    getClubWithMostCompetitors () {
      if (!this.tournamentCompetitors?.length) return null
      const clubCounts = {}
      this.tournamentCompetitors.forEach(competitor => {
        if (competitor.club?.id) {
          const clubId = competitor.club.id
          clubCounts[clubId] = (clubCounts[clubId] || 0) + 1
        }
      })
      const counts = Object.values(clubCounts)
      if (!counts.length) return null
      const maxCount = Math.max(...counts)
      const clubId = Object.keys(clubCounts).find(id => clubCounts[id] === maxCount)
      if (!clubId) return null
      const club = this.tournamentCompetitors.find(c => c.club?.id === parseInt(clubId))?.club
      return club ? { club, count: maxCount } : null
    },

    getClubWithMostIndividualDisciplines () {
      if (!this.tournamentCompetitors?.length) return null
      const clubDisciplines = {}
      this.tournamentCompetitors.forEach(competitor => {
        if (competitor.club?.id) {
          const clubId = competitor.club.id
          const disciplineCount = competitor.categories?.reduce((acc, cat) => acc + (cat.disciplines?.length || 0), 0) || 0
          clubDisciplines[clubId] = (clubDisciplines[clubId] || 0) + disciplineCount
        }
      })
      const counts = Object.values(clubDisciplines)
      if (!counts.length) return null
      const maxCount = Math.max(...counts)
      const clubId = Object.keys(clubDisciplines).find(id => clubDisciplines[id] === maxCount)
      if (!clubId) return null
      const club = this.tournamentCompetitors.find(c => c.club?.id === parseInt(clubId))?.club
      return club ? { club, count: maxCount } : null
    },

    getClubWithMostTeamDisciplines () {
      if (!this.tournamentTeams?.length) return null
      const clubDisciplines = {}
      this.tournamentTeams.forEach(team => {
        if (team.club?.id) {
          const clubId = team.club.id
          const disciplineCount = team.categories?.reduce((acc, cat) => acc + (cat.disciplines?.length || 0), 0) || 0
          clubDisciplines[clubId] = (clubDisciplines[clubId] || 0) + disciplineCount
        }
      })
      const counts = Object.values(clubDisciplines)
      if (!counts.length) return null
      const maxCount = Math.max(...counts)
      const clubId = Object.keys(clubDisciplines).find(id => clubDisciplines[id] === maxCount)
      if (!clubId) return null
      const club = this.tournamentTeams.find(t => t.club?.id === parseInt(clubId))?.club
      return club ? { club, count: maxCount } : null
    },

    updateTournament (tournament) {
      if (tournament.isActive && this.viewingActiveTournament) {
        this.$store.commit('app/setActiveTournament', tournament)
      }
      this.close()
    },

    setPageData () {
      let breadcrumbs = null

      if (!this.viewingActiveTournament) {
        breadcrumbs = [
          {
            text: 'Turniri',
            to: { name: 'Tournaments' }
          },
          {
            text: this.tournament?.name
          }
        ]
      }

      this.$store.commit('app/set', {
        title: this.tournament?.name,
        breadcrumbs,
        showZrebButton: this.tournament?.registrationOpen === false,
        zrebButtonRoute: this.tournament?.registrationOpen === false
          ? { name: 'TournamentBracket', params: { id: this.tournamentId } }
          : null
      })
    },

    async load () {
      this.loading = true
      try {
        await this.$store.dispatch('tournaments/get', this.tournamentId)

        // Load other tournament stuff
        this.loadCompetitors()
        this.loadTeams()
        this.loadHistory()

        this.setPageData()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async loadCompetitors () {
      this.competitorsLoader = true
      try {
        const response = await this.$store.dispatch(
          'tournaments/getCompetitors',
          this.tournamentId
        )
        this.tournamentCompetitors = response || []
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.competitorsLoader = false
      }
    },
    async loadTeams () {
      this.teamsLoader = true
      try {
        const response = await this.$store.dispatch(
          'tournaments/getTeams',
          this.tournamentId
        )
        this.tournamentTeams = response || []
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.teamsLoader = false
      }
    },

    async loadHistory () {
      this.historyLoader = true
      try {
        const response = await this.$store.dispatch(
          'tournaments/getHistory',
          this.tournamentId
        )

        this.history = [
          ...(response?.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          ) || [])
        ]
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.historyLoader = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.grouped-panel {
  margin: 20px 0;
}

@media (min-width: 1050px) {
  .panel {
    width: 100% !important;
  }
}

@media (max-width: 991px) {
  .right-side {
    margin-top: 20px;
  }
}
</style>
